/**
 * Orgs resource — typed wrappers over the platform org endpoints
 * (multi-tenancy Phase F).
 *
 *   POST /v1/platform/orgs            — self-create an org (creator becomes
 *                                       admin + owner). Returns the new Org.
 *   GET  /v1/platform/orgs            — orgs the caller is a member of.
 *
 * These are NOT org-scoped (no X-Org-Id needed): a brand-new user has no
 * active org yet, so the backend resolves the caller from the JWT.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

/** Mirrors orgs.Org (platform-core/internal/orgs/handlers.go). */
export interface Org {
  id: string;
  slug: string;
  name: string;
  parent_org_id?: string | null;
  /**
   * The org's declared purpose. `"real"` is a customer's actual plant — it
   * refuses generated heats and mints no simulated gateways. `"demo"` is a
   * labelled sandbox whose data is sample data. `"rehearsal"` is a copy of a
   * plant's setup where heats are run on the platform, as in a demo.
   *
   * Every surface that shows a number should be able to tell these apart:
   * a sandbox figure read as a measurement is the failure this whole
   * distinction exists to prevent.
   */
  data_mode?: "real" | "demo" | "rehearsal";
  /**
   * The org's physics-twin opt-in (`org_settings.physics_twin_enabled`,
   * migration 0076) — the same flag `/v1/twin/simcore/*` gates on server-side.
   *
   * Only `GET /v1/platform/orgs/{id}` resolves it; `list` and `create` never
   * read it and the server omits the key entirely on those, which is why this
   * is OPTIONAL rather than defaulted. **Absent means UNKNOWN, not "off"** —
   * every consumer must fail CLOSED (hide physics) on `undefined`, so a
   * response that never asked the question can't be read as an answer. Use
   * `usePhysicsTwinEnabled()` (lib/org/physics-twin.ts) rather than reading
   * this field directly.
   */
  physics_twin_enabled?: boolean;
  /**
   * The org's analysis-plan view opt-in (`org_settings.analysis_plan_view`, migration 0169, plant-analytics
   * S14, default off). It gates the Plan tab of the correlations page; a platform admin sets it.
   *
   * Like `physics_twin_enabled`, only `GET /v1/platform/orgs/{id}` resolves it; `list` and `create` omit the
   * key. Absent means UNKNOWN, never "on": consumers fail CLOSED through `useAnalysisPlanViewEnabled()`
   * (lib/org/analysis-plan-view.ts).
   */
  analysis_plan_view?: boolean;
}

/**
 * Mirrors orgs.VendorVisibility (platform-core/internal/orgs/vendor_visibility.go).
 * The active org's opt-out toggle for letting its vendor (parent org) see its
 * instrument reading VALUES. `has_vendor` is false for a top-level org (no
 * vendor parent) — the UI hides the toggle entirely in that case. Default
 * `visible: true` on an org that never touched the toggle. `vendor_name` /
 * `vendor_slug` are null when there is no vendor.
 */
export interface VendorVisibility {
  visible: boolean;
  has_vendor: boolean;
  vendor_name: string | null;
  vendor_slug: string | null;
}

/**
 * Mirrors orgs.ScrapCost (platform-core/internal/orgs/scrap_cost.go) — the money
 * loop's per-org cost of scrap (G4 / S3.4). `null` means NOT priced: reports then
 * show no dollar figure at all, never a fabricated one. Absent and $0 are different
 * facts (a plant whose rejects are free ≠ one that never told us), so these stay
 * nullable rather than defaulting to zero.
 */
export interface ScrapCost {
  cost_per_reject: number | null;
  cost_per_kg: number | null;
  /**
   * `null` = the plant has NOT chosen a currency (WS-F3 / council INV-B3).
   *
   * It used to be a plain `string`, read through `COALESCE(cost_currency,'USD')`
   * over a column that shipped `NOT NULL DEFAULT 'USD'` — and nothing anywhere
   * set it, so every org was labelled USD without ever answering, and its rate
   * was rendered under that label. A rate with no currency is not a price: with
   * this null, every $ figure on the platform is WITHHELD.
   */
  currency: string | null;
}

export const orgsApi = {
  create: (body: { name: string; slug?: string; data_mode?: "real" | "demo" }) =>
    api.post<Org>("/v1/platform/orgs", body),
  list: () => api.get<Paginated<Org>>("/v1/platform/orgs"),
  get: (orgId: string) => api.get<Org>(`/v1/platform/orgs/${orgId}`),
  /**
   * Promote a sandbox to a real plant — `POST /v1/orgs/{id}:promote-to-real`.
   *
   * This is what makes the onboarding wizard's promise ("you can switch a
   * sandbox to a real plant later — the sample data is purged when you do")
   * true. The server purges every simulated Thing, simulated reading and
   * generated genealogy row and THEN flips the mode, in one transaction with
   * the post-condition asserted before commit — so the org is never labelled
   * real while sample data is still readable.
   *
   * ONE-WAY. There is no real → demo direction, by design.
   *
   * Idempotent: an org that is already real returns `already_real: true`.
   */
  promoteToReal: (orgId: string) =>
    api.post<{
      org_id: string;
      data_mode: string;
      already_real: boolean;
      simulated_things_purged: number;
      simulated_readings_purged: number;
      generated_genealogy_rows_purged: number;
      simulator_disabled: boolean;
    }>(`/v1/orgs/${orgId}:promote-to-real`, {}),
  /**
   * Owner self-deletes their OWN org — `DELETE /v1/orgs/{org_id}`. Hard
   * delete: the backend cascades all org data (things, twin model, dashboards,
   * members, …). 200. Owner-only server-side; the SPA additionally gates the
   * affordance on the caller's `is_owner` membership flag. The caller must
   * switch to another org (or route to onboarding) afterwards — the active
   * org no longer exists.
   */
  remove: (orgId: string) => api.delete<void>(`/v1/orgs/${orgId}`),
  /**
   * Change the signed-in user's OWN password (self-service) — `POST
   * /v1/platform/me/change-password`. Authed (JWT sub is the target), NOT
   * org-scoped. 200 `{status:"ok"}` on success. On failure throws
   * ApiClientError carrying the stable code on `.body.error` — shares the auth
   * error vocabulary (see routes/login.tsx): `invalid_credentials` (401) when
   * the current password is wrong, `weak_password` (400) when the new one fails
   * the complexity policy.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ status: string }>("/v1/platform/me/change-password", {
      currentPassword,
      newPassword,
    }),
  /**
   * Set the signed-in person's OWN display name: `PATCH /v1/platform/me`
   * `{display_name}`. The name Forge shows for them and Foundry Scope signs
   * their reports with. Authed (JWT sub is the target), NOT org-scoped. 200
   * `{display_name}` as stored (trimmed, whitespace collapsed). On failure
   * throws ApiClientError with the code on `.body.error`:
   * `invalid_display_name` (400: empty, over 100 characters, a control
   * character, or an '@'; `.body.detail` says which), `account_disabled` (403).
   */
  updateMyDisplayName: (displayName: string) =>
    api.patch<{ display_name: string }>("/v1/platform/me", { display_name: displayName }),
  /**
   * Read the active org's vendor-data-visibility toggle + its vendor identity —
   * `GET /v1/platform/vendor-visibility`. Org-scoped (X-Org-Id). When
   * `has_vendor` is false the org has no vendor parent and the UI shows nothing.
   */
  getVendorVisibility: () =>
    api.get<VendorVisibility>("/v1/platform/vendor-visibility"),
  /**
   * Flip the active org's vendor-data-visibility — `PUT
   * /v1/platform/vendor-visibility` `{visible}`. Admin-only server-side.
   * Returns the same shape as the GET, so callers update local state from the
   * response.
   */
  setVendorVisibility: (visible: boolean) =>
    api.put<VendorVisibility>("/v1/platform/vendor-visibility", { visible }),

  /** Read the active org's scrap cost — `GET /v1/platform/scrap-cost`. */
  getScrapCost: () => api.get<ScrapCost>("/v1/platform/scrap-cost"),
  /**
   * Set the active org's scrap cost — `PUT /v1/platform/scrap-cost`. Admin-only
   * server-side; negative values are rejected. Pass `clear_reject`/`clear_kg` to
   * unprice a basis (set it back to null); omit a field to leave it unchanged.
   */
  setScrapCost: (body: {
    cost_per_reject?: number | null;
    cost_per_kg?: number | null;
    currency?: string;
    clear_reject?: boolean;
    clear_kg?: boolean;
  }) => api.put<ScrapCost>("/v1/platform/scrap-cost", body),
};
